import { dec, isDecimal, mul, mulDivRound, sum, toFixed, toPlain, ZERO, type Dec } from './decimal';
import {
  computeTotals,
  effectiveDiscount,
  type Discount,
  type DocumentTotals,
  type LineTotals,
} from './totals';

/** One line of the counter cart. Prices are per entered unit (the base unit unless changed). */
export interface CartLine {
  key: string;
  variantId: string;
  productId: string;
  /** The variant's sale price per base unit. */
  basePrice: string;
  name: string;
  sku: string;
  barcode: string | null;
  unitId: string;
  unitSymbol: string;
  /** Base units per entered unit. */
  factor: string;
  allowsFraction: boolean;
  /** Sale price × factor: selling below it needs sales.discount. */
  listPrice: string;
  qty: string;
  unitPrice: string;
  discount: Discount | null;
  taxRateId: string | null;
  /** Percent of the chosen tax rate (snapshot for the live totals). */
  taxRate: string;
  /** A combo line: the item picked in each group. Its prices include the picks' extra prices. */
  combo?: ComboPick[] | null;
}

/** One choice of a combo line (its group, item, extra price and own sale price as weight). */
export interface ComboPick {
  groupId: string;
  groupName: string;
  variantId: string;
  name: string;
  extraPrice: string;
  salePrice: string;
}

export interface CartState {
  lines: CartLine[];
  discount: Discount | null;
  /** Bumped on every change so a retried sale keeps its Idempotency-Key only while unchanged. */
  revision: number;
}

export interface CartItem {
  variantId: string;
  productId: string;
  /** Sale price per base unit (defaults to `price`). */
  basePrice?: string;
  name: string;
  sku: string;
  barcode?: string | null;
  unitId: string;
  unitSymbol: string;
  factor?: string;
  allowsFraction: boolean;
  /** Per entered unit; for a combo, its price plus the picks' extra prices. */
  price: string;
  combo?: ComboPick[];
}

/** A unit a line can be sold in; `price` when known (POS feed), else base price × factor. */
export interface SaleUnit {
  unitId: string;
  symbol: string;
  factor: string;
  allowsFraction?: boolean;
  price?: string;
}

/** Sell a line in another unit (piece, box, carton…): its list and unit price follow the unit. */
export function switchUnit(line: CartLine, u: SaleUnit): Partial<CartLine> {
  const list = u.price ?? toPlain(mul(dec(line.basePrice), dec(u.factor)));
  return {
    unitId: u.unitId,
    unitSymbol: u.symbol,
    factor: u.factor,
    ...(u.allowsFraction === undefined ? {} : { allowsFraction: u.allowsFraction }),
    listPrice: list,
    unitPrice: list,
  };
}

const comboKey = (picks: ComboPick[] | null | undefined) =>
  picks ? picks.map((p) => `${p.groupId}:${p.variantId}`).join('|') : '';

/** What a combo line's picks add to its price, per unit. */
export function comboExtras(line: Pick<CartLine, 'combo'>): Dec {
  return sum((line.combo ?? []).map((p) => dec(p.extraPrice)));
}

export type CartAction =
  | { type: 'add'; item: CartItem; qty?: string; tax?: { id: string; rate: string } | null }
  | { type: 'setQty'; key: string; qty: string }
  | { type: 'step'; key: string; by: 1 | -1 }
  | {
      type: 'update';
      key: string;
      patch: Partial<Omit<CartLine, 'key' | 'variantId' | 'productId'>>;
    }
  | { type: 'remove'; key: string }
  | { type: 'discount'; discount: Discount | null }
  | { type: 'taxAll'; tax: { id: string; rate: string } | null }
  | { type: 'clear' }
  | { type: 'restore'; state: CartState };

export const emptyCart: CartState = { lines: [], discount: null, revision: 0 };

let seq = 0;
function lineKey(): string {
  seq += 1;
  return `l${String(Date.now())}-${String(seq)}`;
}

function bump(state: CartState, patch: Partial<CartState>): CartState {
  return { ...state, ...patch, revision: state.revision + 1 };
}

/** The cart reducer: scanning the same item again adds one to its line. */
export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'add': {
      const { item } = action;
      const by = action.qty ?? '1';
      const existing = state.lines.find(
        (l) =>
          l.variantId === item.variantId &&
          l.unitId === item.unitId &&
          comboKey(l.combo) === comboKey(item.combo),
      );
      if (existing)
        return bump(state, {
          lines: state.lines.map((l) =>
            l === existing ? { ...l, qty: toPlain(dec(l.qty) + dec(by)) } : l,
          ),
        });
      const line: CartLine = {
        key: lineKey(),
        variantId: item.variantId,
        productId: item.productId,
        basePrice: item.basePrice ?? item.price,
        name: item.name,
        sku: item.sku,
        barcode: item.barcode ?? null,
        unitId: item.unitId,
        unitSymbol: item.unitSymbol,
        factor: item.factor ?? '1',
        allowsFraction: item.allowsFraction,
        listPrice: item.price,
        qty: by,
        unitPrice: item.price,
        discount: null,
        taxRateId: action.tax?.id ?? null,
        taxRate: action.tax?.rate ?? '0',
        ...(item.combo ? { combo: item.combo } : {}),
      };
      return bump(state, { lines: [...state.lines, line] });
    }
    case 'setQty':
      return bump(state, {
        lines: state.lines.map((l) => (l.key === action.key ? { ...l, qty: action.qty } : l)),
      });
    case 'step':
      return bump(state, {
        lines: state.lines.flatMap((l) => {
          if (l.key !== action.key) return [l];
          const next = dec(l.qty) + dec(String(action.by));
          return next <= 0n ? [] : [{ ...l, qty: toPlain(next) }];
        }),
      });
    case 'update':
      return bump(state, {
        lines: state.lines.map((l) => (l.key === action.key ? { ...l, ...action.patch } : l)),
      });
    case 'remove':
      return bump(state, { lines: state.lines.filter((l) => l.key !== action.key) });
    case 'discount':
      return bump(state, { discount: action.discount });
    case 'taxAll':
      return bump(state, {
        lines: state.lines.map((l) => ({
          ...l,
          taxRateId: action.tax?.id ?? null,
          taxRate: action.tax?.rate ?? '0',
        })),
      });
    case 'clear':
      return { ...emptyCart, revision: state.revision + 1 };
    case 'restore':
      return { ...action.state, revision: state.revision + 1 };
  }
}

export interface PricingSettings {
  decimals: number;
  taxEnabled: boolean;
  pricesIncludeTax: boolean;
}

export function cartTotals(state: CartState, s: PricingSettings): DocumentTotals {
  const parts = state.lines.map((l) => (l.combo?.length ? comboItemLines(l, s.decimals) : [l]));
  const totals = computeTotals(
    parts.flatMap((items, i) =>
      items.map((x) => ({
        qty: isDecimal(x.qty) ? x.qty : '0',
        unitPrice: isDecimal(x.unitPrice) ? x.unitPrice : '0',
        discount: x.discount,
        taxRate: state.lines[i]?.taxRate ?? '0',
      })),
    ),
    { ...s, documentDiscount: state.discount },
  );
  // One total per cart line again: a combo's items add up to its line.
  let at = 0;
  const lines = parts.map((items) => {
    const own = totals.lines.slice(at, at + items.length);
    at += items.length;
    return own.length === 1 && own[0] ? own[0] : mergeLineTotals(own, s.decimals);
  });
  return { ...totals, lines };
}

function mergeLineTotals(lines: LineTotals[], decimals: number): LineTotals {
  const add = (k: 'gross' | 'lineDiscount' | 'documentDiscount' | 'net' | 'tax' | 'total') =>
    toFixed(sum(lines.map((l) => dec(l[k]))), decimals);
  const error = lines.find((l) => l.error)?.error;
  return {
    gross: add('gross'),
    lineDiscount: add('lineDiscount'),
    documentDiscount: add('documentDiscount'),
    net: add('net'),
    taxRate: lines[0]?.taxRate ?? '0',
    tax: add('tax'),
    total: add('total'),
    ...(error ? { error } : {}),
  };
}

/**
 * The lines a combo line becomes on the server (expandCombos): its price without extras split over
 * the picks by their own sale prices (rounded, remainder on the last), each pick's extra added; an
 * amount discount split the same way. Totals use these so the cart matches the invoice.
 */
export function comboItemLines(
  l: CartLine,
  decimals: number,
): {
  variantId: string;
  name: string;
  qty: string;
  unitPrice: string;
  discount: Discount | null;
}[] {
  const picks = l.combo ?? [];
  const price = (isDecimal(l.unitPrice) ? dec(l.unitPrice) : ZERO) - comboExtras(l);
  const weights = picks.map((p) => dec(p.salePrice));
  const shares = allocate(
    price,
    sum(weights) === 0n ? picks.map(() => dec('1')) : weights,
    decimals,
  );
  const discount = effectiveDiscount(l.discount) ?? null;
  const amounts =
    discount?.type === 'AMOUNT' ? allocate(dec(discount.value), shares, decimals) : null;
  return picks.map((p, i) => ({
    variantId: p.variantId,
    name: p.name,
    qty: l.qty,
    unitPrice: toPlain((shares[i] ?? ZERO) + dec(p.extraPrice)),
    discount: amounts ? { type: 'AMOUNT', value: toPlain(amounts[i] ?? ZERO) } : discount,
  }));
}

function allocate(amount: Dec, weights: Dec[], decimals: number): Dec[] {
  const total = sum(weights);
  let left = amount;
  return weights.map((w, i) => {
    if (i === weights.length - 1) return left;
    const share = total === 0n ? ZERO : mulDivRound(amount, w, total, decimals);
    left -= share;
    return share;
  });
}

/** Number of items (sum of quantities) for the cart badge. */
export function itemCount(state: CartState): string {
  return toPlain(state.lines.reduce((s, l) => s + (isDecimal(l.qty) ? dec(l.qty) : ZERO), ZERO));
}

/** A line or the cart gives a discount (lower price or explicit discount): needs sales.discount. */
export function lineNeedsDiscount(line: CartLine): boolean {
  return (
    dec(line.unitPrice) < dec(line.listPrice) || effectiveDiscount(line.discount) !== undefined
  );
}

export function cartNeedsDiscount(state: CartState): boolean {
  return state.lines.some(lineNeedsDiscount) || effectiveDiscount(state.discount) !== undefined;
}

/** Problems that block checkout, in the order a cashier should fix them. */
export function cartProblems(state: CartState, totals: DocumentTotals): string[] {
  const problems: string[] = [];
  for (const l of state.lines) {
    if (!isDecimal(l.qty) || dec(l.qty) <= 0n)
      problems.push(`${l.name}: enter a quantity above 0.`);
    else if (!l.allowsFraction && mul(dec(l.qty), dec(l.factor)) % dec('1') !== 0n)
      problems.push(`${l.name}: ${l.unitSymbol} cannot be split — use a whole number.`);
    if (!isDecimal(l.unitPrice) || dec(l.unitPrice) < 0n)
      problems.push(`${l.name}: enter a valid price.`);
  }
  return [...problems, ...totals.errors];
}

/** Body for POST /counter-sales. Unit prices are sent only when changed (the list price may need 8 decimals). */
export function toCounterSaleRequest(
  state: CartState,
  opts: {
    id: string;
    contactId?: string | null;
    payment?: {
      amountReceived: string;
      methodId?: string | null;
      reference?: string | null;
    } | null;
    notes?: string | null;
  },
) {
  const discount = effectiveDiscount(state.discount);
  return {
    id: opts.id,
    ...(opts.contactId ? { contactId: opts.contactId } : {}),
    ...(discount ? { discount } : {}),
    ...(opts.notes ? { notes: opts.notes } : {}),
    lines: state.lines.map((l) => {
      const lineDiscount = effectiveDiscount(l.discount);
      // A combo's unit price is sent without its picks' extras (the server adds them).
      const extras = comboExtras(l);
      return {
        variantId: l.variantId,
        unitId: l.unitId,
        qty: l.qty,
        ...(dec(l.unitPrice) !== dec(l.listPrice)
          ? { unitPrice: toPlain(dec(l.unitPrice) - extras) }
          : {}),
        ...(l.combo?.length
          ? { combo: l.combo.map((p) => ({ groupId: p.groupId, variantId: p.variantId })) }
          : {}),
        ...(lineDiscount ? { discount: lineDiscount } : {}),
        ...(l.taxRateId ? { taxRateId: l.taxRateId } : {}),
      };
    }),
    ...(opts.payment && dec(opts.payment.amountReceived) > 0n
      ? {
          payment: {
            amountReceived: opts.payment.amountReceived,
            ...(opts.payment.methodId ? { methodId: opts.payment.methodId } : {}),
            ...(opts.payment.reference ? { reference: opts.payment.reference } : {}),
          },
        }
      : {}),
  };
}

/** What the cart needs from a variant (the shape of /variants and /variants/lookup rows). */
export interface VariantLike {
  id: string;
  productId: string;
  displayName: string;
  sku: string;
  barcode: string | null;
  salePrice: string;
  baseUnit: { id: string; symbol: string; allowsFraction: boolean };
}

/** Units of the variant's product, when loaded: the default sale unit is what a scan adds. */
export interface ProductUnitsLike {
  units: { unitId: string; symbol: string; factor: string; isDefaultSale: boolean }[];
}

/** A scanned or picked variant as a cart item, in the product's default sale unit. */
export function cartItemFromVariant(v: VariantLike, product?: ProductUnitsLike | null): CartItem {
  const unit = product?.units.find((u) => u.isDefaultSale && u.unitId !== v.baseUnit.id);
  const factor = unit?.factor ?? '1';
  return {
    variantId: v.id,
    productId: v.productId,
    basePrice: toPlain(dec(v.salePrice)),
    name: v.displayName,
    sku: v.sku,
    barcode: v.barcode,
    unitId: unit?.unitId ?? v.baseUnit.id,
    unitSymbol: unit?.symbol ?? v.baseUnit.symbol,
    factor,
    allowsFraction: v.baseUnit.allowsFraction,
    price: toPlain(unit ? mul(dec(v.salePrice), dec(factor)) : dec(v.salePrice)),
  };
}

/** Parses a saved cart (localStorage); anything malformed gives an empty cart. */
export function parseSavedCart(raw: string | null): CartState {
  if (!raw) return emptyCart;
  try {
    const value = JSON.parse(raw) as Partial<CartState> | null;
    if (!value || !Array.isArray(value.lines)) return emptyCart;
    const lines = value.lines.filter(
      (l): l is CartLine =>
        typeof l === 'object' &&
        typeof l.key === 'string' &&
        typeof l.variantId === 'string' &&
        typeof l.unitId === 'string' &&
        typeof l.qty === 'string' &&
        typeof l.unitPrice === 'string',
    );
    return { lines, discount: value.discount ?? null, revision: 0 };
  } catch {
    return emptyCart;
  }
}
